import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";

export type ToastStatus = "success" | "error" | "info";

interface ToastProps {
	id: string;
	title: string;
	description?: string;
	status: ToastStatus;
	duration?: number;
}

const Toast = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Root>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & {
		status: ToastStatus;
	}
>(({ className = "", status, ...props }, ref) => {
	const statusStyles = {
		success: "bg-role-green-bg border-role-green text-role-green",
		error: "bg-role-red-bg border-role-red text-role-red",
		info: "bg-role-blue-bg border-role-blue text-role-blue",
	};

	return (
		<ToastPrimitives.Root
			ref={ref}
			className={`
				group pointer-events-auto relative flex w-full items-center justify-between
				space-x-4 overflow-hidden rounded-lg border-l-4 p-6 pr-8 shadow-lg transition-all
				animate-in animate-out
				${statusStyles[status]} ${className}
			`}
			{...props}
		/>
	);
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Action>, React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>>(({ className = "", ...props }, ref) => (
	<ToastPrimitives.Action
		ref={ref}
		className={`
			inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent
			px-3 text-sm font-medium ring-offset-white transition-colors hover:bg-secondary/10
			focus:outline-none focus:ring-2 focus:ring-neutral focus:ring-offset-2 disabled:pointer-events-none
			disabled:opacity-50 ${className}
		`}
		{...props}
	/>
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Close>, React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>>(({ className = "", ...props }, ref) => (
	<ToastPrimitives.Close
		ref={ref}
		className={`
			absolute right-2 top-2 rounded-md p-1 opacity-0 transition-opacity hover:opacity-100
			focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100
			${className}
		`}
		toast-close=""
		{...props}
	>
		<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<line x1="18" y1="6" x2="6" y2="18"></line>
			<line x1="6" y1="6" x2="18" y2="18"></line>
		</svg>
	</ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Title>, React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>>(({ className = "", ...props }, ref) => (
	<ToastPrimitives.Title ref={ref} className={`text-sm font-semibold ${className}`} {...props} />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Description>, React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>>(
	({ className = "", ...props }, ref) => <ToastPrimitives.Description ref={ref} className={`text-sm opacity-90 ${className}`} {...props} />,
);
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export { type ToastProps, type ToastActionElement, ToastPrimitives, Toast, ToastAction, ToastClose, ToastTitle, ToastDescription };
